import React, { useCallback, useRef, useState } from 'react'

// MUI Imports
import Chip from '@mui/material/Chip'
import { createFilterOptions } from '@mui/material/Autocomplete'

// Components Imports
import CustomAutocomplete from './CustomAutocomplete'
import CustomTextField from './CustomTextField'

const SELECT_ALL_OPTION = { __selectAll: true }
const defaultFilter = createFilterOptions()

const AutoCompleteChipWithSelectAll = (props) => {
  const {
    value,
    onChange,
    options,
    getOptionLabel,
    placeholder,
    label,
    chipLabelKey,
    inputProperties,
    selectAllLabel = 'Select All',
    filterOptions: customFilterOptions,
    renderOption: customRenderOption,
    isOptionEqualToValue: customIsOptionEqualToValue,
    limitTags = 2,
    renderInput: customRenderInput,
    ...restProps
  } = props

  const filteredOptionsRef = useRef([])
  const [open, setOpen] = useState(false)

  const handleFilterOptions = useCallback((opts, state) => {
    const filterFn = customFilterOptions || defaultFilter
    const filtered = filterFn(opts, state)
    filteredOptionsRef.current = filtered

    if (filtered.length > 0) {
      return [SELECT_ALL_OPTION, ...filtered]
    }
    return filtered
  }, [customFilterOptions])

  const handleChange = useCallback((event, newValue, reason, details) => {
    if (details?.option?.__selectAll) {
      const currentValue = value || []
      const filteredOpts = filteredOptionsRef.current

      const allFilteredSelected = filteredOpts.every(opt =>
        currentValue.some(v => v === opt || (chipLabelKey && v?.[chipLabelKey] === opt?.[chipLabelKey]))
      )

      if (allFilteredSelected) {
        const newSelection = currentValue.filter(v =>
          !filteredOpts.some(opt => v === opt || (chipLabelKey && v?.[chipLabelKey] === opt?.[chipLabelKey]))
        )
        onChange(event, newSelection, 'removeOption', details)
      } else {
        const existingSet = new Set(currentValue.map(v => chipLabelKey ? v?.[chipLabelKey] : v))
        const toAdd = filteredOpts.filter(opt => !existingSet.has(chipLabelKey ? opt?.[chipLabelKey] : opt))
        onChange(event, [...currentValue, ...toAdd], 'selectOption', details)
      }
      return
    }
    onChange(event, newValue, reason, details)
  }, [onChange, value, chipLabelKey])

  const getOptionLabelWrapper = useCallback((option) => {
    if (option?.__selectAll) return selectAllLabel
    return getOptionLabel ? getOptionLabel(option) : (typeof option === 'string' ? option : '')
  }, [getOptionLabel, selectAllLabel])

  const renderOptionWrapper = useCallback((optionProps, option, state) => {
    if (option?.__selectAll) {
      return (
        <li {...optionProps} key="__select-all" style={{ borderBottom: '1px solid #e0e0e0' }}>
          <span style={{ fontWeight: 600 }}>
            {selectAllLabel}
          </span>
        </li>
      )
    }

    if (customRenderOption) {
      return customRenderOption(optionProps, option, state)
    }

    return (
      <li {...optionProps}>
        {getOptionLabel ? getOptionLabel(option) : option}
      </li>
    )
  }, [selectAllLabel, customRenderOption, getOptionLabel])

  const isOptionEqualToValue = useCallback((option, val) => {
    if (option?.__selectAll) return false
    if (customIsOptionEqualToValue) {
      return customIsOptionEqualToValue(option, val)
    }
    if (chipLabelKey) {
      return option?.[chipLabelKey] === val?.[chipLabelKey]
    }
    return option === val
  }, [customIsOptionEqualToValue, chipLabelKey])

  const handleChipClick = useCallback((e) => {
    e.stopPropagation()
    setOpen(true)
  }, [])

  const renderLimitedTags = useCallback((tagValue, getTagProps) => {
    const displayLimit = limitTags > 0 ? limitTags : tagValue.length
    const displayTags = tagValue.slice(0, displayLimit)
    const remainingCount = tagValue.length - displayLimit

    return (
      <>
        {displayTags.map((option, index) => {
          const tagProps = getTagProps({ index })
          return (
            <Chip
              label={option?.[chipLabelKey]}
              {...tagProps}
              key={index}
              size='small'
              onClick={handleChipClick}
            />
          )
        })}
        {remainingCount > 0 && (
          <span
            style={{ fontSize: '0.875rem', marginLeft: 4, cursor: 'pointer' }}
            onClick={handleChipClick}
          >
            +{remainingCount}
          </span>
        )}
      </>
    )
  }, [limitTags, chipLabelKey, handleChipClick])

  return (
    <CustomAutocomplete
      multiple
      value={value}
      options={options}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionLabel={getOptionLabelWrapper}
      filterOptions={handleFilterOptions}
      renderInput={params => {
        const hasValue = value?.length > 0
        if (customRenderInput) {
          const inputElement = customRenderInput(params)
          if (hasValue && inputElement?.props) {
            return React.cloneElement(inputElement, {
              ...inputElement.props,
              placeholder: ''
            })
          }
          return inputElement
        }
        return (
          <CustomTextField
            {...params}
            label={label}
            placeholder={hasValue ? '' : placeholder}
            {...inputProperties}
          />
        )
      }}
      onChange={handleChange}
      renderOption={renderOptionWrapper}
      isOptionEqualToValue={isOptionEqualToValue}
      disableCloseOnSelect
      renderTags={renderLimitedTags}
      {...restProps}
    />
  )
}

export default AutoCompleteChipWithSelectAll
